import { useState, useEffect, useRef } from "react";
import { Plus, X, Clapperboard, Play, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText, BACKEND_URL } from "@/lib/api";

const CATEGORIES = ["Classic", "Trending", "Luxury", "Festival", "Exclusive"];
const TIERS = ["small", "medium", "luxury", "legendary"];

export default function AdminGifts() {
  const [gifts, setGifts] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", emoji: "", price: 100, category: "Classic", tier: "", diamond_value: 0, schedule_start: "", schedule_end: "" });
  const [icon, setIcon] = useState(null);
  const [video, setVideo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const rowVideoRef = useRef(null);
  const rowTargetRef = useRef(null);

  const load = () => adminApi.get("/admin/gifts").then(({ data }) => setGifts(data.gifts)).catch(() => setGifts([]));
  useEffect(() => { load(); }, []);

  const toggle = async (g) => {
    try {
      await adminApi.put(`/admin/gifts/${g.id}`, { active: !g.active });
      setGifts((gs) => gs.map((x) => (x.id === g.id ? { ...x, active: !x.active } : x)));
      toast.success(`${g.name} is now ${!g.active ? "ACTIVE — visible in gift tray" : "inactive"}`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const updatePrice = async (g, price) => {
    if (!price || price === g.price) return;
    try {
      const { data } = await adminApi.put(`/admin/gifts/${g.id}`, { price: Number(price) });
      setGifts((gs) => gs.map((x) => (x.id === g.id ? data.gift : x)));
      toast.success("Price updated");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (icon) fd.append("icon", icon);
      if (video) fd.append("video", video);
      await adminApi.post("/admin/gifts", fd);
      toast.success(video ? "Video FX gift created — plays fullscreen when sent! 🎬" : "Gift created — live in the app tray instantly!");
      setShowForm(false);
      setForm({ name: "", emoji: "", price: 100, category: "Classic", tier: "", diamond_value: 0, schedule_start: "", schedule_end: "" });
      setIcon(null);
      setVideo(null);
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const pickRowVideo = (g) => {
    rowTargetRef.current = g;
    rowVideoRef.current?.click();
  };

  const uploadRowVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const g = rowTargetRef.current;
    if (!file || !g) return;
    setUploadingId(g.id);
    try {
      const fd = new FormData();
      fd.append("video", file);
      const { data } = await adminApi.post(`/admin/gifts/${g.id}/video`, fd);
      setGifts((gs) => gs.map((x) => (x.id === g.id ? data.gift : x)));
      toast.success(`${g.name} now has a video effect 🎬`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setUploadingId(null);
    }
  };

  const removeVideo = async (g) => {
    try {
      const { data } = await adminApi.delete(`/admin/gifts/${g.id}/video`);
      setGifts((gs) => gs.map((x) => (x.id === g.id ? data.gift : x)));
      toast.success("Video effect removed");
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="admin-gifts">
      <input ref={rowVideoRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={uploadRowVideo} data-testid="row-video-input" />
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-extrabold font-display">Gift Manager</h1>
        <button data-testid="new-gift-button" onClick={() => setShowForm(true)}
          className="gradient-primary text-white text-xs font-bold px-4 py-2 rounded-full flex items-center gap-1.5"><Plus size={14} /> New Gift</button>
      </div>

      {preview && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" data-testid="gift-video-preview" onClick={() => setPreview(null)}>
          <div className="relative w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <button data-testid="preview-close" onClick={() => setPreview(null)} className="absolute -top-9 right-0 text-zinc-300"><X size={20} /></button>
            <video src={`${BACKEND_URL}${preview.video_url}`} autoPlay loop controls playsInline className="w-full rounded-2xl bg-black" />
            <p className="text-center text-sm font-bold mt-3 text-white">{preview.name} — fullscreen effect preview</p>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <form onSubmit={create} className="glass bg-[#14141c] rounded-3xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-bold font-display">Upload Custom Gift</h2>
              <button type="button" data-testid="gift-form-close" onClick={() => setShowForm(false)}><X size={18} className="text-zinc-400" /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="gift-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Gift name"
                className="w-full bg-[#1A1A24] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-violet-500" />
              <div className="flex gap-2">
                <input data-testid="gift-emoji-input" value={form.emoji} onChange={(e) => set("emoji", e.target.value)} placeholder="Emoji (e.g. 🎁)"
                  className="flex-1 bg-[#1A1A24] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-violet-500" />
                <input data-testid="gift-price-input" required type="number" min={1} value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="Coin price"
                  className="flex-1 bg-[#1A1A24] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-violet-500" />
              </div>
              <div className="flex gap-2">
                <select data-testid="gift-category-select" value={form.category} onChange={(e) => set("category", e.target.value)}
                  className="flex-1 bg-[#1A1A24] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
                <select data-testid="gift-tier-select" value={form.tier} onChange={(e) => set("tier", e.target.value)}
                  className="flex-1 bg-[#1A1A24] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none">
                  <option value="">Auto tier (by price)</option>
                  {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <input data-testid="gift-diamond-input" type="number" min={0} value={form.diamond_value} onChange={(e) => set("diamond_value", e.target.value)} placeholder="Ruby value (0 = auto 50%)"
                className="w-full bg-[#1A1A24] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-violet-500" />
              <div>
                <label className="text-[10px] text-zinc-500 font-semibold">Icon image (PNG/WebP/GIF, optional)</label>
                <input data-testid="gift-icon-input" type="file" accept="image/*" onChange={(e) => setIcon(e.target.files?.[0])}
                  className="w-full text-xs text-zinc-400 mt-1 file:bg-white/10 file:border-0 file:rounded-lg file:px-3 file:py-1.5 file:text-zinc-300 file:mr-2" />
              </div>
              <div className="rounded-xl border border-violet-500/30 bg-violet-500/10 p-3">
                <label className="text-[10px] text-violet-300 font-bold flex items-center gap-1"><Clapperboard size={12} /> EFFECT VIDEO (MP4/WebM, optional)</label>
                <p className="text-[10px] text-zinc-500 mt-0.5 mb-1.5">Plays fullscreen for everyone in the room when this gift is sent — like Tango's animated gifts. Transparent WebM works best.</p>
                <input data-testid="gift-video-input" type="file" accept="video/mp4,video/webm,video/quicktime" onChange={(e) => setVideo(e.target.files?.[0])}
                  className="w-full text-xs text-zinc-400 file:bg-violet-500/30 file:border-0 file:rounded-lg file:px-3 file:py-1.5 file:text-violet-200 file:mr-2" />
                {video && <p className="text-[10px] text-emerald-400 mt-1 font-semibold">🎬 {video.name} ({(video.size / 1024 / 1024).toFixed(1)} MB)</p>}
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-[10px] text-zinc-500 font-semibold">Schedule start (optional)</label>
                  <input data-testid="gift-schedule-start" type="datetime-local" value={form.schedule_start} onChange={(e) => set("schedule_start", e.target.value)}
                    className="w-full bg-[#1A1A24] border border-white/10 rounded-xl px-3 py-2 text-xs outline-none [color-scheme:dark]" />
                </div>
                <div className="flex-1">
                  <label className="text-[10px] text-zinc-500 font-semibold">Schedule end (optional)</label>
                  <input data-testid="gift-schedule-end" type="datetime-local" value={form.schedule_end} onChange={(e) => set("schedule_end", e.target.value)}
                    className="w-full bg-[#1A1A24] border border-white/10 rounded-xl px-3 py-2 text-xs outline-none [color-scheme:dark]" />
                </div>
              </div>
              <button data-testid="gift-create-submit" disabled={busy} className="w-full gradient-primary text-white font-bold py-3 rounded-xl disabled:opacity-40">
                {busy ? "Creating…" : "Create Gift"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="glass rounded-2xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-zinc-500 text-left border-b border-white/10">
              <th className="p-3">Gift</th><th>Category</th><th>Tier</th><th>Price</th><th>Rubies</th><th>FX</th><th>Sent</th><th>Revenue</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {gifts?.map((g) => (
              <tr key={g.id} className="border-b border-white/5" data-testid={`gift-row-${g.id}`}>
                <td className="p-3 flex items-center gap-2">
                  {g.icon_url ? <img src={`${BACKEND_URL}${g.icon_url}`} alt="" className="w-7 h-7 object-contain" /> : <span className="text-xl">{g.emoji}</span>}
                  <span className="text-white font-semibold">{g.name}</span>
                  {g.video_url && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-violet-500/25 text-violet-300 flex items-center gap-0.5" data-testid={`fx-badge-${g.id}`}><Clapperboard size={9} />FX</span>}
                </td>
                <td className="text-zinc-400">{g.category}</td>
                <td><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${g.tier === "legendary" ? "bg-amber-500/20 text-amber-400" : g.tier === "luxury" ? "bg-pink-500/20 text-pink-400" : "bg-white/10 text-zinc-300"}`}>{g.tier}</span></td>
                <td>
                  <input type="number" defaultValue={g.price} data-testid={`gift-price-edit-${g.id}`}
                    onBlur={(e) => updatePrice(g, e.target.value)}
                    className="w-20 bg-[#1A1A24] border border-white/10 rounded-lg px-2 py-1 text-amber-400 font-bold outline-none focus:border-violet-500" />
                </td>
                <td className="text-cyan-300 font-semibold">{g.diamond_value}</td>
                <td>
                  <div className="flex items-center gap-1">
                    {g.video_url ? (
                      <>
                        <button data-testid={`fx-preview-${g.id}`} title="Preview effect" onClick={() => setPreview(g)}
                          className="p-1.5 rounded-lg bg-violet-500/20 text-violet-300 hover:bg-violet-500/30"><Play size={12} /></button>
                        <button data-testid={`fx-remove-${g.id}`} title="Remove video" onClick={() => removeVideo(g)}
                          className="p-1.5 rounded-lg bg-white/5 text-zinc-500 hover:text-red-400"><Trash2 size={12} /></button>
                      </>
                    ) : (
                      <button data-testid={`fx-upload-${g.id}`} title="Add effect video" onClick={() => pickRowVideo(g)} disabled={uploadingId === g.id}
                        className="p-1.5 rounded-lg bg-white/5 text-zinc-400 hover:text-violet-300 disabled:opacity-40">
                        {uploadingId === g.id ? <span className="text-[9px] font-bold">…</span> : <Upload size={12} />}
                      </button>
                    )}
                  </div>
                </td>
                <td className="text-zinc-400">{g.times_sent?.toLocaleString()}</td>
                <td className="text-emerald-400 font-semibold">{g.revenue?.toLocaleString()}</td>
                <td className="pr-3">
                  <button data-testid={`gift-toggle-${g.id}`} onClick={() => toggle(g)}
                    className={`w-10 h-5.5 rounded-full p-0.5 transition-colors ${g.active ? "gradient-primary" : "bg-white/10"}`} style={{ height: 22 }}>
                    <span className={`block w-4.5 h-4.5 bg-white rounded-full transition-transform ${g.active ? "translate-x-5" : ""}`} style={{ width: 18, height: 18 }} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
